import { useClerk } from '@clerk/react-router';
import { captureException } from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchStreamLink, httpLink, loggerLink, splitLink, type HTTPHeaders } from '@trpc/client';
import { useState, type ReactElement, type ReactNode } from 'react';
import superjson from 'superjson';
import { api } from '../../utils/api.ts';

export type TRPCProviderProps = {
    children: ReactNode;
};

function captureTrpcError(error: unknown, path: string | undefined, input: unknown): void {
    captureException(error, {
        contexts: {
            trpc: { path, input },
        },
    });
}

export function TRPCProvider({ children }: TRPCProviderProps): ReactElement {
    const clerk = useClerk();

    const [queryClient] = useState(() => {
        return new QueryClient({
            queryCache: new QueryCache({
                onError: (error, query) => {
                    const [path, options] = query.queryKey as [unknown, unknown];
                    captureTrpcError(
                        error,
                        Array.isArray(path) ? path.join('.') : String(path),
                        (options as { input?: unknown } | undefined)?.input,
                    );
                },
            }),
            mutationCache: new MutationCache({
                onError: (error, variables, _context, mutation) => {
                    captureTrpcError(error, mutation.options.mutationKey?.flat().join('.'), variables);
                },
            }),
            defaultOptions: {
                queries: { refetchOnWindowFocus: false },
            },
        });
    });

    const [trpcClient] = useState(() => {
        const headers = async (): Promise<HTTPHeaders> => {
            const token = await clerk.session?.getToken();
            if (!clerk.session || !token) {
                window.location.href = '/sign-in';
                throw new Error('No active session');
            }
            return {
                Authorization: `Bearer ${token}`,
                'x-clerk-user-id': clerk.session.user.id,
            };
        };

        return api.createClient({
            links: [
                loggerLink({
                    enabled: (opts) =>
                        import.meta.env.DEV || (opts.direction === 'down' && opts.result instanceof Error),
                }),
                splitLink({
                    // platoAiChat procedures stream async-generator responses,
                    // which only work over httpBatchStreamLink.
                    condition: (op) => op.path.startsWith('platoAiChat.'),
                    true: httpBatchStreamLink({
                        transformer: superjson,
                        url: '/api/trpc',
                        headers,
                    }),
                    false: httpLink({
                        transformer: superjson,
                        url: '/api/trpc',
                        headers,
                    }),
                }),
            ],
        });
    });

    return (
        <api.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </api.Provider>
    );
}
